import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Sparkles,
  ClipboardCheck,
  Stethoscope,
  User,
} from 'lucide-react';
import type { Client, SessionType } from 'shared';
import { api, ApiError } from '@/lib/api';
import { AppShell } from '@/components/layout/AppShell';
import { Header } from '@/components/layout/Header';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Spinner';
import { cn } from '@/lib/cn';

/* ==========================================================================
   NewCheckup — Session type selection page.

   Route: /clients/:clientId/new-checkup
   Select session type -> POST /api/checkups -> navigate to /checkups/:newId
   ========================================================================== */

/* --------------------------------------------------------------------------
   Constants
   -------------------------------------------------------------------------- */

interface SessionTypeOption {
  type: SessionType;
  label: string;
  description: string;
  icon: typeof Sparkles;
  color: string;
  bgColor: string;
}

const SESSION_OPTIONS: SessionTypeOption[] = [
  {
    type: 'welcome',
    label: 'Welcome Checkup',
    description: 'Prima visita e accoglienza della nuova cliente. Raccolta dati anagrafici, consensi e analisi iniziale.',
    icon: ClipboardCheck,
    color: 'text-status-info',
    bgColor: 'bg-status-info-light',
  },
  {
    type: 'beauty',
    label: 'Beauty Checkup',
    description: 'Analisi approfondita di viso e corpo. Misurazioni, foto, valutazione della pelle e proposte personalizzate.',
    icon: Sparkles,
    color: 'text-accent-primary',
    bgColor: 'bg-accent-primary-light',
  },
  {
    type: 'kinesiology',
    label: 'Kinesiologia',
    description: 'Valutazione posturale e muscolare. Analisi delle tensioni e delle zone critiche.',
    icon: Stethoscope,
    color: 'text-accent-secondary',
    bgColor: 'bg-accent-secondary-light',
  },
  {
    type: 'medical',
    label: 'Medico Estetico',
    description: 'Valutazione medico-estetica approfondita. Anamnesi, diagnosi e piano di trattamento.',
    icon: Stethoscope,
    color: 'text-status-positive',
    bgColor: 'bg-status-positive-light',
  },
];

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export function NewCheckup() {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();

  const [client, setClient] = useState<Client | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [creating, setCreating] = useState<SessionType | null>(null);
  const [error, setError] = useState<string | null>(null);

  // ------------------------------------------------------------------
  // Fetch client data
  // ------------------------------------------------------------------

  useEffect(() => {
    if (!clientId) return;
    setIsLoading(true);

    api
      .get<Client>(`/clients/${clientId}`)
      .then((data) => {
        setClient(data);
      })
      .catch((err) => {
        if (err instanceof ApiError) {
          setError(err.status === 404 ? 'Cliente non trovata.' : err.message);
        } else {
          setError('Errore di connessione.');
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [clientId]);

  // ------------------------------------------------------------------
  // Create checkup
  // ------------------------------------------------------------------

  const handleSelect = useCallback(
    async (sessionType: SessionType) => {
      if (!clientId) return;
      setCreating(sessionType);
      setError(null);

      try {
        const result = await api.post<{ id: number }>('/checkups', {
          clientId: Number(clientId),
          sessionType,
        });
        navigate(`/checkups/${result.id}`, { replace: true });
      } catch (err) {
        if (err instanceof ApiError) {
          setError(err.message);
        } else {
          setError('Errore nella creazione del check-up. Riprova.');
        }
        setCreating(null);
      }
    },
    [clientId, navigate],
  );

  // ------------------------------------------------------------------
  // Loading
  // ------------------------------------------------------------------

  if (isLoading) {
    return (
      <AppShell header={<Header showBack title="Nuovo Check-up" />}>
        <div className="space-y-6">
          {/* Client info skeleton */}
          <div className="flex items-center gap-3">
            <Skeleton circle width={48} height={48} />
            <div className="flex-1 space-y-2">
              <Skeleton height={18} className="w-2/5" />
              <Skeleton height={14} className="w-1/3" />
            </div>
          </div>
          {/* Cards skeleton */}
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} height={100} className="w-full rounded-[var(--radius-lg)]" />
            ))}
          </div>
        </div>
      </AppShell>
    );
  }

  // ------------------------------------------------------------------
  // Error / no client
  // ------------------------------------------------------------------

  if (error && !client) {
    return (
      <AppShell header={<Header showBack title="Nuovo Check-up" />}>
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div
            className={cn(
              'mb-5 flex h-16 w-16 items-center justify-center',
              'rounded-full bg-status-negative-light',
            )}
          >
            <User className="h-7 w-7 text-status-negative" strokeWidth={1.5} />
          </div>
          <p className="text-lg font-[family-name:var(--font-heading)] font-semibold text-text-primary">
            {error}
          </p>
          <Button variant="secondary" className="mt-6" onClick={() => navigate(-1)}>
            Torna indietro
          </Button>
        </div>
      </AppShell>
    );
  }

  // ------------------------------------------------------------------
  // Main render
  // ------------------------------------------------------------------

  const clientName = client ? `${client.nome} ${client.cognome}` : '';

  return (
    <AppShell header={<Header showBack title="Nuovo Check-up" />}>
      {/* Client info */}
      {client && (
        <div className="mb-6">
          <p className="text-sm text-text-muted">Stai creando un check-up per</p>
          <p className="font-[family-name:var(--font-heading)] text-xl font-bold text-text-primary mt-1">
            {clientName}
          </p>
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div className="mb-4 rounded-[var(--radius-lg)] border border-status-negative/20 bg-status-negative-light p-4">
          <p className="text-sm text-status-negative font-medium">{error}</p>
        </div>
      )}

      {/* Session type heading */}
      <h2 className="font-[family-name:var(--font-heading)] text-lg font-bold text-text-primary mb-4">
        Seleziona il tipo di sessione
      </h2>

      {/* Session type cards */}
      <div className="space-y-3">
        {SESSION_OPTIONS.map((option) => {
          const Icon = option.icon;
          const isCreating = creating === option.type;
          const isDisabled = creating !== null && !isCreating;

          return (
            <button
              key={option.type}
              onClick={() => handleSelect(option.type)}
              disabled={isDisabled || isCreating}
              className={cn(
                'flex w-full items-center gap-4',
                'px-5 py-5 rounded-[var(--radius-lg)]',
                'border border-border-default bg-surface-card',
                'text-left shadow-xs',
                'transition-all duration-[var(--duration-normal)] ease-[var(--ease-out)]',
                'cursor-pointer',
                'hover:shadow-sm hover:-translate-y-0.5 hover:border-accent-primary',
                'active:translate-y-0 active:shadow-xs',
                'disabled:opacity-50 disabled:pointer-events-none',
                'min-h-[var(--touch-comfortable)]',
              )}
            >
              {/* Icon */}
              <div
                className={cn(
                  'flex h-14 w-14 items-center justify-center shrink-0',
                  'rounded-[var(--radius-md)]',
                  option.bgColor,
                )}
              >
                {isCreating ? (
                  <div className="skeleton h-6 w-6 rounded-full" />
                ) : (
                  <Icon className={cn('h-7 w-7', option.color)} strokeWidth={1.5} />
                )}
              </div>

              {/* Text */}
              <div className="flex-1 min-w-0">
                <p className="font-[family-name:var(--font-heading)] text-base font-semibold text-text-primary">
                  {option.label}
                </p>
                <p className="text-sm text-text-muted mt-1 leading-relaxed">
                  {option.description}
                </p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Bottom spacer */}
      <div className="h-8" />
    </AppShell>
  );
}
